import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Activity, BarChart2, Trophy, ClipboardList, Home, Sword, TrendingUp, Brain, User, Lock, Sparkles } from "lucide-react";
import DojoThemeSwitcher from "@/components/DojoThemeSwitcher";
import Clock from "@/components/Clock";
import AudioToggle from "@/components/AudioToggle";
import CursorToggle from "@/components/CursorToggle";
import { playSfx } from "@/lib/audio";
import { useAuth, usePremium } from "@/contexts/AuthContext";

const NAV_LINKS = [
  { path: "/", label: "Home", icon: Home, color: "var(--bc-accent)" },
  { path: "/miei-pronostici", label: "I Miei", icon: ClipboardList, color: "rgba(59,130,246,0.8)", testid: "nav-miei" },
  { path: "/samurai/ronin", label: "Ronin", icon: Trophy, color: "var(--bc-accent-2)", testid: "nav-ronin" },
  { path: "/samurai", label: "Samurai Picker", icon: Sword, color: "rgba(220,38,38,0.85)", testid: "nav-samurai" },
  { path: "/daymio-trends", label: "Daymio Trends", icon: TrendingUp, color: "rgba(249,115,22,0.85)", testid: "nav-daymio", premiumOnly: true },
  { path: "/brain-cracker", label: "Brain Cracker", icon: Brain, color: "#22FFA3", testid: "nav-brain-cracker" },
  { path: "/stats", label: "Stats", icon: BarChart2, color: "rgba(245,158,11,0.8)", testid: "nav-stats" },
];

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [version, setVersion] = useState(null);
  const { isAuthenticated } = useAuth();
  const { isPremium } = usePremium();

  useEffect(() => {
    let cancelled = false;
    const API = process.env.REACT_APP_BACKEND_URL;
    fetch(`${API}/api/version`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!cancelled && data?.version) setVersion(data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <nav
      className="sticky top-0 z-50"
      style={{
        background: "var(--bc-panel, var(--bc-panel, rgba(2,2,2,0.92)))",
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        borderBottom: "1px solid var(--bc-border, rgba(255,255,255,0.07))",
      }}
    >
      <div className="max-w-7xl mx-auto px-4 h-14 flex items-center justify-between">
        {/* Logo */}
        <button
          data-testid="nav-logo"
          onClick={() => navigate("/")}
          className="flex items-center gap-2.5 group"
          style={{ transition: "opacity 0.15s" }}
          onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.8")}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
        >
          <div
            className="bc-brand-square w-8 h-8 flex items-center justify-center relative"
            style={{ background: "#22C55E", clipPath: "polygon(12% 0%, 88% 0%, 100% 12%, 100% 88%, 88% 100%, 12% 100%, 0% 88%, 0% 12%)" }}
          >
            <Activity size={15} color="#020202" strokeWidth={3} />
          </div>
          <div className="flex flex-col leading-none">
            <span
              className="font-heading font-black text-lg uppercase tracking-[0.18em]"
              style={{ color: "var(--bc-ink, #FFFFFF)", letterSpacing: "0.15em" }}
            >
              BET
              <span className="bc-brand-accent" style={{ color: "#22C55E" }}> CRACKER</span>
            </span>
            <span
              className="font-data text-[9px] uppercase tracking-[0.2em] hidden sm:block"
              style={{ color: "rgba(255,255,255,0.3)" }}
            >
              ANALISI CALCISTICA
            </span>
          </div>
          {version?.version && (
            <span
              data-testid="nav-version-badge"
              title={version.commit ? `Build ${version.version} · ${version.commit}` : version.version}
              className="bc-version-pill font-data text-[9px] uppercase tracking-[0.15em] px-1.5 py-0.5 ml-1 rounded-sm hidden sm:inline-block"
              style={{
                color: "rgba(34,197,94,0.9)",
                background: "rgba(34,197,94,0.08)",
                border: "1px solid rgba(34,197,94,0.2)",
                letterSpacing: "0.12em",
                lineHeight: 1,
              }}
            >
              {version.version}
            </span>
          )}
        </button>

        {/* Nav Links */}
        <div className="flex items-center gap-1">
          {NAV_LINKS.filter((l) => l.path !== "/").map(({ path, label, icon: Icon, color, testid, premiumOnly }) => {
            const isActive = location.pathname === path;
            const showLock = premiumOnly && !isPremium;
            return (
              <button
                key={path}
                data-testid={testid}
                onClick={() => { playSfx("click"); navigate(path); }}
                className="flex items-center gap-1.5 px-3 py-1.5 relative"
                style={{
                  background: isActive ? "var(--bc-border, rgba(255,255,255,0.07))" : "transparent",
                  border: isActive ? "1px solid var(--bc-border, rgba(255,255,255,0.1))" : "1px solid transparent",
                  color: isActive ? "#FFFFFF" : "rgba(161,161,170,0.9)",
                  transition: "all 0.15s",
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.background = "var(--bc-panel-elev, rgba(255,255,255,0.04))";
                    e.currentTarget.style.color = "#FFFFFF";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.color = "rgba(161,161,170,0.9)";
                  }
                }}
              >
                <Icon size={13} style={{ color: isActive ? color : "inherit" }} />
                <span
                  className="font-heading font-bold text-xs uppercase tracking-wider hidden sm:inline"
                >
                  {label}
                </span>
                {showLock && (
                  <Lock size={9} style={{ color: "#C8FF00", marginLeft: 2 }} />
                )}
                {isActive && (
                  <span
                    className="absolute bottom-0 left-0 right-0 h-px"
                    style={{ background: color }}
                  />
                )}
              </button>
            );
          })}
          {/* Account / Auth / Pricing CTA */}
          {isAuthenticated ? (
            <button
              data-testid="nav-account"
              onClick={() => { playSfx("click"); navigate("/account"); }}
              className="flex items-center gap-1.5 px-2.5 sm:px-3 py-2 sm:py-1.5"
              style={{
                background: isPremium
                  ? "rgba(200,255,0,0.10)"
                  : (location.pathname === "/account" ? "var(--bc-border, rgba(255,255,255,0.07))" : "rgba(255,255,255,0.04)"),
                border: isPremium ? "1px solid rgba(200,255,0,0.45)" : "1px solid rgba(255,255,255,0.10)",
                color: isPremium ? "#C8FF00" : "#E4E4E7",
                transition: "all 0.15s",
                minHeight: 36,
              }}
              title={isPremium ? "Account · Premium attivo" : "Account"}
              aria-label={isPremium ? "Account Premium" : "Account"}
            >
              {isPremium ? <Sparkles size={15} /> : <User size={15} />}
              <span className="font-heading font-bold text-xs uppercase tracking-wider">
                {isPremium ? "Premium" : "Account"}
              </span>
            </button>
          ) : (
            <>
              <button
                data-testid="nav-auth"
                onClick={() => { playSfx("click"); navigate("/auth"); }}
                className="flex items-center gap-1.5 px-2.5 sm:px-3 py-2 sm:py-1.5"
                style={{
                  background: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.18)",
                  color: "#E4E4E7",
                  transition: "all 0.15s",
                  minHeight: 36,
                }}
                title="Accedi o registrati"
                aria-label="Accedi"
              >
                <User size={15} />
                <span className="font-heading font-bold text-xs uppercase tracking-wider">
                  Accedi
                </span>
              </button>
              <button
                data-testid="nav-pricing"
                onClick={() => { playSfx("click"); navigate("/pricing"); }}
                className="flex items-center gap-1.5 px-2.5 sm:px-3 py-2 sm:py-1.5"
                style={{
                  background: "rgba(200,255,0,0.10)",
                  border: "1px solid rgba(200,255,0,0.45)",
                  color: "#C8FF00",
                  transition: "all 0.15s",
                  boxShadow: "0 0 14px rgba(200,255,0,0.18)",
                  minHeight: 36,
                }}
                title="Scopri Premium"
                aria-label="Premium"
              >
                <Sparkles size={15} />
                <span className="font-heading font-bold text-xs uppercase tracking-wider hidden xs:inline sm:inline">
                  Premium
                </span>
              </button>
            </>
          )}
          <DojoThemeSwitcher />
          <AudioToggle />
          <CursorToggle />
          <Clock />
        </div>
      </div>

      {/* Laser line at bottom */}
      <div className="h-px laser-line opacity-30" />
    </nav>
  );
}
